import axios, { AxiosInstance } from 'axios';

const API_BASE = 'https://api.flipdish.co/api/v1.0';
const ORG_API_BASE = 'https://api.flipdish.co/orgManagement';

export type Currency = 'EUR' | 'USD' | 'GBP';

export interface FlipdishConfig {
  appId: string;
  orgId: string;
  templateSalesChannelId: string;
  orderfulApi: any;
}

export interface StoreAddress {
  addressLine1: string;
  addressLine2: string;
  addressLine3?: string; // Required for UK (area/town); not used for IE or US
  city: string;
  postcode: string;
  countryCode: string;
  state?: string; // Required for US (two-char USPS abbreviation e.g. "CA")
}

export interface StoreCreationInput {
  name: string;
  currency: Currency;
  phone: string;
  email: string;
  address: StoreAddress;
  latitude: string;
  longitude: string;
  skipMenu?: boolean;
}

export interface MannaStoreInput {
  cellId: string;
  position: string;
  leadTime: string;
  travelTime: string;
  category: string;
  landingZoneId: string;
  orderHrLimit: string;
  preorderAvail: boolean;
  autoAcceptEnabled: boolean;
  autoPreparingEnabled: boolean;
  autoPreparedEnabled: boolean;
}

export interface FlipdishStoreGroup {
  StoreGroupId: number;
  Name: string;
  Currency: string;
  [key: string]: any;
}

export interface FlipdishMenu {
  MenuId: number;
  Name?: string;
  [key: string]: any;
}

export interface MenuSection {
  MenuSectionId: number;
  Name: string;
  MenuItems?: MenuItem[];
  [key: string]: any;
}

export interface MenuItem {
  MenuItemId: number;
  Name: string;
  Price: number;
  [key: string]: any;
}

export interface StoreCreationResult {
  storeId: number | null;
  salesChannelId: string;
  storeGroupId: number;
  propertyId: string;
  menuId: number | null;
  portalUrl: string;
  partnerName?: string;
}

// ---------------------------------------------------------------------------
// Auth — client credentials (machine-to-machine)
// ---------------------------------------------------------------------------

async function getAccessToken(orderfulApiInstance: any): Promise<string> {
  if (typeof orderfulApiInstance?.getFlipdishToken !== 'function') {
    throw new Error(
      'orderfulApi.getFlipdishToken is not available — ensure @m4a3/api-client is updated to a version that includes this method.'
    );
  }
  const res = await orderfulApiInstance.getFlipdishToken();
  const token = res?.accessToken ?? res?.access_token ?? res;
  if (typeof token !== 'string' || !token) {
    const safePreview = typeof res === 'object' && res !== null
      ? JSON.stringify(Object.keys(res))
      : typeof res;
    throw new Error(
      `getFlipdishToken returned an unexpected response shape. Keys present: ${safePreview}`
    );
  }
  return token;
}

function createClient(accessToken: string): AxiosInstance {
  return axios.create({
    baseURL: API_BASE,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
  });
}

function createOrgClient(accessToken: string): AxiosInstance {
  return axios.create({
    baseURL: ORG_API_BASE,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
  });
}

// ---------------------------------------------------------------------------
// Org Management API v3.0 — Properties, Sales Channels
// ---------------------------------------------------------------------------

interface CreatePropertyPayload {
  name: string;
  countryCode: string;
  emailAddress: string;
  phoneNumber?: string;
  addressFields: Record<string, string>;
  coordinates?: { latitude: number; longitude: number };
  type: 'Store' | 'CentralKitchen';
}

interface CreatePropertyResponse {
  propertyId: string;
  name: string;
  [key: string]: any;
}

interface CloneSalesChannelResponse {
  salesChannelId: string;
  name: string;
  type: string;
  propertyId: string;
  brandId: string;
  storeId: number | null;
  menuId: string;
  isPublished: boolean;
  [key: string]: any;
}

async function createProperty(
  orgClient: AxiosInstance,
  orgId: string,
  payload: CreatePropertyPayload
): Promise<CreatePropertyResponse> {
  const res = await orgClient.post(`/orgs/${orgId}/properties`, payload);
  return res.data.data;
}

// The URL structure is: /properties/{targetPropertyId}/salesChannels/{templateSalesChannelId}/clone
// Despite the path segments, the propertyId here is the DESTINATION property that will receive the
// cloned sales channel. The templateSalesChannelId identifies the source SC to clone from.
async function cloneSalesChannel(
  orgClient: AxiosInstance,
  orgId: string,
  targetPropertyId: string,
  templateSalesChannelId: string,
  type: string = 'FlipdishWebApp'
): Promise<CloneSalesChannelResponse> {
  const res = await orgClient.post(
    `/orgs/${orgId}/properties/${targetPropertyId}/salesChannels/${templateSalesChannelId}/clone`,
    { type }
  );
  return res.data.data;
}

async function updateSalesChannel(
  orgClient: AxiosInstance,
  orgId: string,
  propertyId: string,
  salesChannelId: string,
  updates: { name?: string; menuId?: string; phoneNumber?: string; usePropertyPhoneNumber?: boolean }
): Promise<void> {
  await orgClient.post(
    `/orgs/${orgId}/properties/${propertyId}/salesChannels/${salesChannelId}`,
    updates
  );
}

async function publishSalesChannel(
  orgClient: AxiosInstance,
  orgId: string,
  propertyId: string,
  salesChannelId: string
): Promise<void> {
  await orgClient.post(
    `/orgs/${orgId}/properties/${propertyId}/salesChannels/${salesChannelId}/publish`
  );
}

async function getSalesChannel(
  orgClient: AxiosInstance,
  orgId: string,
  propertyId: string,
  salesChannelId: string
): Promise<CloneSalesChannelResponse> {
  const res = await orgClient.get(
    `/orgs/${orgId}/properties/${propertyId}/salesChannels/${salesChannelId}`
  );
  return res.data.data;
}

async function updatePropertyOpeningHours(
  orgClient: AxiosInstance,
  orgId: string,
  propertyId: string,
  hours: { day: string; open: string; close: string; type: string; dispatchTypes: string[] }[]
): Promise<void> {
  await orgClient.post(
    `/orgs/${orgId}/properties/${propertyId}/openingHours`,
    hours
  );
}

function buildAllDayOpeningHours() {
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  return days.flatMap(day => [
    { day, open: '00:00:00', close: '23:59:00', type: 'TimeSlot', dispatchTypes: ['Collection'] },
    { day, open: '00:00:00', close: '23:59:00', type: 'TimeSlot', dispatchTypes: ['Delivery'] },
  ]);
}

function buildIEAddressFields(address: StoreAddress): Record<string, string> {
  return {
    line1: address.addressLine1,
    line2: address.addressLine2,
    line3: address.city,
    eirCode: address.postcode,
  };
}

function buildUKAddressFields(address: StoreAddress): Record<string, string> {
  return {
    line1: address.addressLine1,
    line2: address.addressLine2,
    line3: address.addressLine3 || '',
    postCode: address.postcode,
  };
}

function buildUSAddressFields(address: StoreAddress): Record<string, string> {
  return {
    address: address.addressLine1,
    city: address.city,
    state: address.state || '',
    zipCode: address.postcode,
  };
}

function buildAddressFields(address: StoreAddress): Record<string, string> {
  switch (address.countryCode) {
    case 'IE': return buildIEAddressFields(address);
    case 'GB': return buildUKAddressFields(address);
    case 'US': return buildUSAddressFields(address);
    default: throw new Error(`Unsupported country code: ${address.countryCode}. Supported: IE, GB, US.`);
  }
}

// ---------------------------------------------------------------------------
// Public API v1.0 — Store Groups, Menus
// ---------------------------------------------------------------------------

export async function createStoreGroup(
  client: AxiosInstance,
  appId: string,
  name: string,
  currency: Currency
): Promise<FlipdishStoreGroup> {
  const res = await client.post(`/${appId}/storegroups`, {
    Name: name,
    Currency: currency,
  });
  return res.data.Data;
}

export async function assignStoresToGroup(
  client: AxiosInstance,
  appId: string,
  storeGroupId: number,
  storeIds: number[]
): Promise<void> {
  await client.post(`/${appId}/storegroups/${storeGroupId}/assignStores`, storeIds);
}

export async function updateStoreAddress(
  client: AxiosInstance,
  storeId: number,
  address: StoreAddress
): Promise<void> {
  await client.post(`/stores/${storeId}/address`, {
    Line1: address.addressLine1,
    Line2: address.addressLine2 || undefined,
    City: address.city,
    Postcode: address.postcode,
    CountryCode: address.countryCode,
  });
}

export async function getStoresByApp(
  client: AxiosInstance,
  appId: string
): Promise<{ StoreId: number; [key: string]: any }[]> {
  const stores: any[] = [];
  let page = 1;
  while (true) {
    const res = await client.get(`/${appId}/stores`, { params: { page } });
    const batch = res.data.Data || [];
    stores.push(...batch);
    const total = res.data.TotalRecordCount || 0;
    if (stores.length >= total || batch.length === 0) break;
    page++;
  }
  return stores;
}

export async function createMenu(
  client: AxiosInstance,
  appId: string,
  name: string
): Promise<FlipdishMenu> {
  const res = await client.post(`/${appId}/menus`, { Name: name });
  const raw = typeof res.data === 'number' ? res.data : res.data?.Data?.MenuId;
  const menuId = typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
  if (menuId === null) {
    throw new Error(
      `createMenu: expected a numeric menu ID from Flipdish but got: ${JSON.stringify(res.data)}`
    );
  }
  await client.post(`/menus/${menuId}`, {
    MenuSectionBehaviour: 'ExpandMultiple',
  });
  return { MenuId: menuId, Name: name };
}

export async function assignMenuToStore(
  client: AxiosInstance,
  storeId: number,
  menuId: number
): Promise<void> {
  await client.post(`/stores/${storeId}/menu/${menuId}`);
}

export async function getMenuSections(
  client: AxiosInstance,
  menuId: number
): Promise<MenuSection[]> {
  const res = await client.get(`/menus/${menuId}/sections`);
  return res.data.Data || [];
}

export async function createMenuSection(
  client: AxiosInstance,
  menuId: number,
  name: string
): Promise<MenuSection> {
  const res = await client.post(`/menus/${menuId}/sections`, { Name: name });
  return res.data.Data;
}

export async function getSectionItems(
  client: AxiosInstance,
  menuId: number,
  sectionId: number
): Promise<MenuItem[]> {
  const res = await client.get(`/menus/${menuId}/sections/${sectionId}/sectionitems`);
  return res.data.Data || [];
}

export async function createSectionItem(
  client: AxiosInstance,
  menuId: number,
  sectionId: number,
  item: { Name: string; Price: number }
): Promise<MenuItem> {
  const res = await client.post(
    `/menus/${menuId}/sections/${sectionId}/sectionitems`,
    item
  );
  return res.data.Data;
}

export async function updateSectionItem(
  client: AxiosInstance,
  menuId: number,
  sectionId: number,
  itemId: number,
  updates: { Name?: string; Price?: number }
): Promise<MenuItem> {
  const res = await client.post(
    `/menus/${menuId}/sections/${sectionId}/sectionitems/${itemId}`,
    updates
  );
  return res.data.Data;
}

const DELIVERY_SECTION_NAME = 'Delivery Section';
const DELIVERY_FEE_ITEM_NAME = 'DELIVERY_FEE';
const DELIVERY_FEE_PRICE = 4.99;

export async function ensureDeliveryFee(
  client: AxiosInstance,
  menuId: number
): Promise<void> {
  const sections = await getMenuSections(client, menuId);
  let deliverySection = sections.find(s => s.Name === DELIVERY_SECTION_NAME);

  if (!deliverySection) {
    deliverySection = await createMenuSection(client, menuId, DELIVERY_SECTION_NAME);
  }

  const items = await getSectionItems(client, menuId, deliverySection.MenuSectionId);
  const feeItem = items.find(i => i.Name === DELIVERY_FEE_ITEM_NAME);

  if (!feeItem) {
    await createSectionItem(client, menuId, deliverySection.MenuSectionId, {
      Name: DELIVERY_FEE_ITEM_NAME,
      Price: DELIVERY_FEE_PRICE,
    });
  } else if (feeItem.Price !== DELIVERY_FEE_PRICE) {
    await updateSectionItem(client, menuId, deliverySection.MenuSectionId, feeItem.MenuItemId, {
      Price: DELIVERY_FEE_PRICE,
    });
  }
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export async function withRetry<T>(
  fn: () => Promise<T>,
  maxAttempts: number = 5,
  delayMs: number = 10_000,
  onRetry?: (attempt: number, maxAttempts: number, error: any) => void
): Promise<T> {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (err) {
      if (attempt === maxAttempts) throw err;
      if (onRetry) onRetry(attempt, maxAttempts, err);
      await sleep(delayMs);
    }
  }
  throw new Error('Unreachable');
}

const POLL_INTERVAL_MS = 15_000;
const POLL_MAX_ATTEMPTS = 40;

async function waitForStoreId(
  orgClient: AxiosInstance,
  orgId: string,
  propertyId: string,
  salesChannelId: string,
  onProgress?: (attempt: number, maxAttempts: number) => void
): Promise<number> {
  for (let attempt = 1; attempt <= POLL_MAX_ATTEMPTS; attempt++) {
    if (onProgress) onProgress(attempt, POLL_MAX_ATTEMPTS);
    try {
      const sc = await getSalesChannel(orgClient, orgId, propertyId, salesChannelId);
      if (sc.storeId != null) {
        return sc.storeId;
      }
    } catch {
      // Sales channel may not be queryable yet — Flipdish propagation delay
    }
    await sleep(POLL_INTERVAL_MS);
  }
  throw new Error(
    `Store ID did not appear on sales channel ${salesChannelId} after ${Math.round(POLL_MAX_ATTEMPTS * POLL_INTERVAL_MS / 1000 / 60)} minutes. Check the Flipdish portal.`
  );
}

// ---------------------------------------------------------------------------
// Orchestrator
// ---------------------------------------------------------------------------

export type StepCallback = (stepIndex: number, status: 'start' | 'done' | 'error', detail?: string) => void;

export interface PartialCreationState {
  propertyId?: string;
  salesChannelId?: string;
  salesChannelRenamed?: boolean;
  storeId?: number;
  storeGroupId?: number;
  storeGroupAssigned?: boolean;
  menuId?: number;
  menuDeliveryFeeSet?: boolean;
  failedAtStep?: number;
  partnerId?: number;
  logoUrl?: string;
  largeLogoUrl?: string;
  posId?: number;
}

export async function createStore(
  config: FlipdishConfig,
  input: StoreCreationInput,
  onStep?: StepCallback,
  onPartialState?: (state: PartialCreationState) => void,
  resumeState?: PartialCreationState,
  debugFailAtStep?: number | string
): Promise<StoreCreationResult> {
  const notify = onStep || (() => {});
  const retrying = (step: number) => (attempt: number, max: number) => {
    notify(step, 'start', `Flipdish is taking a moment — this is normal. Retrying automatically... (${attempt}/${max})`);
  };
  const partial: PartialCreationState = { ...resumeState };
  const emitPartial = () => { if (onPartialState) onPartialState({ ...partial }); };
  const startFrom = resumeState?.failedAtStep ?? 0;

  const maybeFailAt = (step: number | string) => {
    if (debugFailAtStep !== undefined && String(debugFailAtStep) === String(step)) {
      throw new Error(`[DEBUG] Forced failure at step ${step}`);
    }
  };

  const RETRIES = 10;
  const DELAY = 10_000;
  const CLONE_DELAY = 20_000;

  let accessToken: string;
  let client: AxiosInstance;
  let orgClient: AxiosInstance;

  try {
    // Step 0: Always re-authenticate to ensure a fresh token (tokens may expire between retries)
    notify(0, 'start');
    maybeFailAt(0);
    accessToken = await withRetry(
      () => getAccessToken(config.orderfulApi),
      RETRIES, DELAY, retrying(0)
    );
    client = createClient(accessToken);
    orgClient = createOrgClient(accessToken);
    notify(0, 'done', startFrom > 0 ? 'Re-authenticated (fresh token)' : 'Authenticated via client credentials');
  } catch (err: any) {
    partial.failedAtStep = 0;
    emitPartial();
    throw err;
  }

  let propertyId = partial.propertyId ?? '';
  try {
    // Step 1: Create Property
    if (!propertyId) {
      notify(1, 'start');
      maybeFailAt(1);
      const addressFields = buildAddressFields(input.address);
      const propertyPayload: CreatePropertyPayload = {
        name: input.name,
        countryCode: input.address.countryCode,
        emailAddress: input.email,
        phoneNumber: input.phone || undefined,
        addressFields,
        type: 'Store',
      };
      if (input.latitude || input.longitude) {
        const lat = Number.parseFloat(input.latitude);
        const lng = Number.parseFloat(input.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          throw new Error(
            `Invalid coordinates: latitude "${input.latitude}" and longitude "${input.longitude}" must both be valid numbers.`
          );
        }
        propertyPayload.coordinates = { latitude: lat, longitude: lng };
      }
      const property = await withRetry(
        () => createProperty(orgClient, config.orgId, propertyPayload),
        RETRIES, DELAY, retrying(1)
      );
      propertyId = property.propertyId;
      partial.propertyId = propertyId;
      emitPartial();
      notify(1, 'done', `Property created → ${propertyId}`);
    } else if (propertyId) {
      notify(1, 'done', `Property ${propertyId} (resumed)`);
    }
  } catch (err: any) {
    partial.failedAtStep = 1;
    emitPartial();
    throw err;
  }

  try {
    // Step 2: Set property opening hours
    if (startFrom <= 2) {
      notify(2, 'start');
      maybeFailAt(2);
      const allDayHours = buildAllDayOpeningHours();
      await withRetry(
        () => updatePropertyOpeningHours(orgClient, config.orgId, propertyId, allDayHours),
        RETRIES, DELAY, retrying(2)
      );
      notify(2, 'done', 'Property opening hours set to all day (Collection + Delivery)');
    } else {
      notify(2, 'done', 'Opening hours (resumed)');
    }
  } catch (err: any) {
    partial.failedAtStep = 2;
    emitPartial();
    throw err;
  }

  let salesChannelId = partial.salesChannelId ?? '';
  let clonedThisRun = false;
  try {
    // Step 3: Clone golden template sales channel
    if (!salesChannelId) {
      notify(3, 'start');
      maybeFailAt(3);
      clonedThisRun = true;
      const clonedSC = await withRetry(
        () => cloneSalesChannel(orgClient, config.orgId, propertyId, config.templateSalesChannelId, 'FlipdishWebApp'),
        30, CLONE_DELAY, retrying(3)
      );
      salesChannelId = clonedSC.salesChannelId;
      partial.salesChannelId = salesChannelId;
      emitPartial();
    }
    if (salesChannelId && !partial.salesChannelRenamed) {
      if (!clonedThisRun) notify(3, 'start');
      maybeFailAt('3b');
      await withRetry(
        () => updateSalesChannel(orgClient, config.orgId, propertyId, salesChannelId, {
          name: input.name,
          usePropertyPhoneNumber: true,
        }),
        RETRIES, DELAY, retrying(3)
      );
      partial.salesChannelRenamed = true;
      emitPartial();
      notify(3, 'done', `Sales Channel cloned → ${salesChannelId}, renamed to "${input.name}"`);
    } else if (salesChannelId && partial.salesChannelRenamed) {
      notify(3, 'done', `Sales Channel ${salesChannelId} (resumed)`);
    }
  } catch (err: any) {
    partial.failedAtStep = 3;
    emitPartial();
    throw err;
  }

  try {
    // Step 4: Publish sales channel
    if (startFrom <= 4) {
      notify(4, 'start');
      maybeFailAt(4);
      await withRetry(
        () => publishSalesChannel(orgClient, config.orgId, propertyId, salesChannelId),
        RETRIES, DELAY, retrying(4)
      );
      notify(4, 'done', `Sales Channel ${salesChannelId} published`);
    } else {
      notify(4, 'done', `Sales Channel published (resumed)`);
    }
  } catch (err: any) {
    partial.failedAtStep = 4;
    emitPartial();
    throw err;
  }

  let storeId = partial.storeId ?? 0;
  try {
    // Step 5: Wait for store provisioning
    if (!storeId) {
      notify(5, 'start');
      maybeFailAt(5);
      storeId = await waitForStoreId(
        orgClient, config.orgId, propertyId, salesChannelId,
        (attempt, max) => {
          notify(5, 'start', `Flipdish is provisioning the store — this is normal and can take up to 10 minutes. Please wait... (${attempt}/${max})`);
        }
      );
      partial.storeId = storeId;
      emitPartial();
      notify(5, 'done', `Store provisioned → Store ID: ${storeId}`);
    } else if (storeId) {
      notify(5, 'done', `Store ID: ${storeId} (resumed)`);
    }
  } catch (err: any) {
    partial.failedAtStep = 5;
    emitPartial();
    throw err;
  }

  let storeGroupId = partial.storeGroupId ?? 0;
  let groupCreatedThisRun = false;
  try {
    // Step 6: Create Sales Channel Group and assign store
    if (!storeGroupId) {
      notify(6, 'start');
      maybeFailAt(6);
      groupCreatedThisRun = true;
      const storeGroup = await withRetry(
        () => createStoreGroup(client, config.appId, input.name, input.currency),
        RETRIES, DELAY, retrying(6)
      );
      storeGroupId = storeGroup.StoreGroupId;
      partial.storeGroupId = storeGroupId;
      emitPartial();
    }
    if (storeGroupId && !partial.storeGroupAssigned) {
      if (!groupCreatedThisRun) notify(6, 'start');
      maybeFailAt('6b');
      await withRetry(
        () => assignStoresToGroup(client, config.appId, storeGroupId, [storeId]),
        RETRIES, DELAY, retrying(6)
      );
      partial.storeGroupAssigned = true;
      emitPartial();
      notify(6, 'done', `Sales Channel Group ${storeGroupId} created, store ${storeId} assigned`);
    } else if (storeGroupId && partial.storeGroupAssigned) {
      notify(6, 'done', `Sales Channel Group ${storeGroupId} (resumed)`);
    }
  } catch (err: any) {
    partial.failedAtStep = 6;
    emitPartial();
    throw err;
  }

  let menuId = partial.menuId ?? 0;
  let menuCreatedThisRun = false;
  if (input.skipMenu) {
    notify(7, 'done', 'Menu creation skipped');
    notify(8, 'done', 'Menu assignment skipped');
  } else {
    try {
      // Step 7: Create menu with delivery fee
      const menuName = `${input.name} Menu`;
      if (!menuId) {
        notify(7, 'start');
        maybeFailAt(7);
        menuCreatedThisRun = true;
        const newMenu = await withRetry(
          () => createMenu(client, config.appId, menuName),
          RETRIES, DELAY, retrying(7)
        );
        menuId = newMenu.MenuId;
        partial.menuId = menuId;
        emitPartial();
      }
      if (menuId && !partial.menuDeliveryFeeSet) {
        if (!menuCreatedThisRun) notify(7, 'start');
        maybeFailAt('7b');
        await withRetry(
          () => ensureDeliveryFee(client, menuId),
          RETRIES, DELAY, retrying(7)
        );
        partial.menuDeliveryFeeSet = true;
        emitPartial();
        notify(7, 'done', `Menu ${menuId} created with delivery fee ("${menuName}")`);
      } else if (menuId && partial.menuDeliveryFeeSet) {
        notify(7, 'done', `Menu ${menuId} (resumed)`);
      }
    } catch (err: any) {
      partial.failedAtStep = 7;
      emitPartial();
      throw err;
    }

    try {
      // Step 8: Assign menu to store
      if (startFrom <= 8) {
        notify(8, 'start');
        maybeFailAt(8);
        await withRetry(
          () => assignMenuToStore(client, storeId, menuId),
          RETRIES, DELAY, retrying(8)
        );
        notify(8, 'done', `Menu ${menuId} assigned to store ${storeId}`);
      } else {
        notify(8, 'done', `Menu assigned (resumed)`);
      }
    } catch (err: any) {
      partial.failedAtStep = 8;
      emitPartial();
      throw err;
    }
  }

  try {
    // Step 9: Update store address
    if (startFrom <= 9) {
      notify(9, 'start');
      maybeFailAt(9);
      await withRetry(
        () => updateStoreAddress(client, storeId, input.address),
        RETRIES, DELAY, retrying(9)
      );
      notify(9, 'done', `Address set: ${input.address.addressLine1}, ${input.address.city}`);
    } else {
      notify(9, 'done', `Address set (resumed)`);
    }
  } catch (err: any) {
    partial.failedAtStep = 9;
    emitPartial();
    throw err;
  }

  try {
    // Step 10: Verify store under app
    if (startFrom <= 10) {
      notify(10, 'start');
      maybeFailAt(10);
      const appStores = await withRetry(
        () => getStoresByApp(client, config.appId),
        RETRIES, DELAY, retrying(10)
      );
      const found = appStores.some(s => s.StoreId === storeId);
      if (!found) {
        throw new Error(
          `Store ${storeId} was created but is NOT listed under app ${config.appId}. Check the Flipdish portal.`
        );
      }
      notify(10, 'done', `Store "${input.name}" verified under ${config.appId}`);
    } else {
      notify(10, 'done', `Store verified (resumed)`);
    }
  } catch (err: any) {
    partial.failedAtStep = 10;
    emitPartial();
    throw err;
  }

  return {
    storeId,
    salesChannelId,
    storeGroupId,
    propertyId,
    menuId: input.skipMenu ? null : menuId,
    portalUrl: `https://portal.flipdish.com/${config.appId}/properties/${propertyId}`,
  };
}
